/**
 * `<DispatchForm>` — the canonical-action dispatch form, extracted from the
 * since-retired `/actions` page (d7c697dc) so it can be reused wherever staff
 * need to invoke an action with hand-entered input (today the `/status`
 * per-card "Run with data…" modal).
 *
 * Compiles the action's `input_schema` to a form tree (`compileSchemaToFormTree`),
 * renders a `<SchemaForm>`, and dispatches through `POST /api/dispatch/:action`
 * (the same chokepoint, as `human:staff`) via the `useDispatch` hook. Saga
 * wrapper actions (`book_session` etc.) get the 202 + saga-run polling UX.
 * `delete_member` keeps its type-DELETE confirmation. An optional `onDispatched`
 * callback fires after a successful dispatch (the Status board uses it to
 * refresh the affected card's "last run").
 */

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { CatalogActionEntry } from "@layer/dashboard/types";
import {
  compileSchemaToFormTree,
  type FormObjectNode,
  type FormTreeNode,
} from "@layer/dashboard/schema-form/compiler";
import { useDispatch, type DispatchErrorState } from "../lib/use-dispatch";
import { failureText, type FormRows } from "../lib/field-errors";
import type { DispatchOptions } from "../lib/dispatch";
import { useSagaRun } from "../lib/use-saga-run";
import { SchemaForm } from "./SchemaForm";
import { useLookupRefresh } from "../lib/lookup-refresh";

export const SAGA_WRAPPER_ACTIONS = new Set([
  "book_session",
  "cancel_booking",
  "reschedule_booking",
]);

/**
 * Actions that require an extra type-DELETE confirmation before dispatch. The
 * SchemaForm surfaces the modal — callers just configure the prompt.
 */
export const CONFIRM_DISPATCH_ACTIONS: Record<
  string,
  { prompt: string; confirmKey: string }
> = {
  delete_member: {
    prompt:
      "Type DELETE to confirm. This nulls the member's PII permanently and scrubs their stored message history for identifiers no other member shares. Member's bookings + audit_log stay intact.",
    confirmKey: "DELETE",
  },
};

export interface DispatchFormProps {
  readonly action: CatalogActionEntry;
  /** Fires after a dispatch resolves `ok` (e.g. to refresh a status card). */
  readonly onDispatched?: () => void;
}

export function DispatchForm({
  action,
  onDispatched,
}: DispatchFormProps): JSX.Element {
  const dispatch = useDispatch<
    Record<string, unknown>,
    Record<string, unknown>
  >(action.name);
  const isSagaWrapper = SAGA_WRAPPER_ACTIONS.has(action.name);
  const { bump: bumpLookupRefresh } = useLookupRefresh();

  const sagaRunId = useMemo<string | null>(() => {
    if (!isSagaWrapper || dispatch.result === null) return null;
    const id = dispatch.result["run_id"];
    return typeof id === "string" ? id : null;
  }, [isSagaWrapper, dispatch.result]);
  const sagaState = useSagaRun(sagaRunId);

  // Silent-refresh trigger: for saga wrappers the dispatch only returns
  // `{run_id, status: 'queued'}` — actual canonical state changes land
  // when the saga reaches `done`. Bump the lookup-refresh counter then
  // so sibling cards' dropdowns reflect the post-saga rows.
  useEffect(() => {
    if (isSagaWrapper && sagaState.state === "done") {
      bumpLookupRefresh();
    }
  }, [isSagaWrapper, sagaState.state, bumpLookupRefresh]);

  // Compile the action's JSON Schema to a form tree once per action.
  const formTree = useMemo<FormTreeNode | null>(() => {
    if (
      action.input_schema === null ||
      typeof action.input_schema !== "object"
    ) {
      return null;
    }
    return compileSchemaToFormTree(
      action.input_schema as Record<string, unknown>,
    );
  }, [action.input_schema]);

  const onSubmit = (value: Record<string, unknown>): void => {
    const options: DispatchOptions = {};
    void dispatch.run(value, options).then((r) => {
      // Sync dispatches (non-saga) write canonical state inside the
      // chokepoint; bump as soon as the response lands so sibling cards'
      // dropdowns pick up the new rows. Saga wrappers also resolve `ok`
      // here (the wrapper returned `{queued}`); the real state change
      // arrives at the `done` transition handled above. Bumping on both
      // is harmless — the second bump is a near-instant refetch.
      if (r.ok) {
        bumpLookupRefresh();
        onDispatched?.();
      }
    });
  };

  if (
    formTree === null ||
    (formTree.kind !== "object" && formTree.kind !== "oneOf")
  ) {
    return (
      <p className="schema-form-unsupported">
        Action&apos;s input_schema could not be compiled to a form tree —
        falling back to no input.
      </p>
    );
  }
  // Empty-schema (no fields) → no form, dispatch as-is with `{}`. For
  // a oneOf root we always render the form (variants have fields).
  const hasFields =
    formTree.kind === "oneOf"
      ? formTree.variants.length > 0
      : Object.keys((formTree as FormObjectNode).properties).length > 0;
  const confirm = CONFIRM_DISPATCH_ACTIONS[action.name];

  return (
    <>
      {hasFields ? (
        <SchemaForm
          tree={formTree}
          piiFields={action.pii_fields}
          onSubmit={onSubmit}
          busy={dispatch.loading}
          {...(confirm !== undefined ? { confirmDispatch: confirm } : {})}
        />
      ) : (
        <div className="schema-form-buttons">
          <p className="action-no-input">No input — dispatch as-is.</p>
          <button
            type="button"
            className="action-dispatch-btn"
            onClick={() => onSubmit({})}
            disabled={dispatch.loading}
          >
            {dispatch.loading ? "Dispatching…" : "Dispatch"}
          </button>
        </div>
      )}
      {(dispatch.result !== null || dispatch.error !== null) && (
        <button
          type="button"
          onClick={() => dispatch.reset()}
          className="action-reset-btn"
        >
          Clear
        </button>
      )}
      <ActionResult
        loading={dispatch.loading}
        error={dispatch.error}
        rows={formRows(formTree)}
        result={dispatch.result}
        replayed={dispatch.replayed}
        lastRequestId={dispatch.lastRequestId}
      />
      {isSagaWrapper && sagaRunId !== null ? (
        <SagaRunPanel runId={sagaRunId} state={sagaState} />
      ) : null}
    </>
  );
}

interface SagaRunPanelProps {
  readonly runId: string;
  readonly state: ReturnType<typeof useSagaRun>;
}

function SagaRunPanel({ runId, state }: SagaRunPanelProps): JSX.Element {
  const elapsed = useElapsed(state.started_at);
  if (state.state === "in_progress") {
    return (
      <div className="action-result action-result-running">
        <p>
          <strong>Saga running…</strong>{" "}
          <span className="tag">attempt {state.attempts}</span>{" "}
          <span className="tag">{elapsed}</span>
        </p>
        <p className="action-request-id">
          run_id: <code>{runId}</code>
        </p>
      </div>
    );
  }
  if (state.state === "done") {
    return (
      <div className="action-result action-result-ok">
        <p>
          <strong>Saga done</strong>{" "}
          <span className="tag">
            {state.attempts} attempt{state.attempts === 1 ? "" : "s"}
          </span>
        </p>
        <p className="action-request-id">
          run_id: <code>{runId}</code>
        </p>
      </div>
    );
  }
  if (state.state === "failed") {
    return (
      <div className="action-result action-result-error">
        <p>
          <strong>Saga failed</strong>{" "}
          <code className="tag tag-error">
            {state.last_error ?? "unknown_error"}
          </code>
        </p>
        <p className="action-request-id">
          run_id: <code>{runId}</code>
        </p>
      </div>
    );
  }
  if (state.state === "forbidden") {
    return (
      <div className="action-result action-result-error">
        <p>
          <strong>Saga status check refused</strong>{" "}
          <code className="tag tag-error">{state.last_error ?? "forbidden"}</code>
        </p>
        <p>
          The action was dispatched and runs regardless — this account was
          refused permission to READ its status. Inspect{" "}
          <Link to={`/data/scheduled_actions`}>/data/scheduled_actions</Link>{" "}
          for the outcome.
        </p>
        <p className="action-request-id">
          run_id: <code>{runId}</code>
        </p>
      </div>
    );
  }
  return (
    <div className="action-result action-result-error">
      <p>
        <strong>Saga polling timed out</strong>
      </p>
      <p>
        After 60 seconds the run still hadn&apos;t reached a terminal state.
        Inspect{" "}
        <Link to={`/data/scheduled_actions`}>/data/scheduled_actions</Link> for
        the current row state.
      </p>
      <p className="action-request-id">
        run_id: <code>{runId}</code>
      </p>
    </div>
  );
}

function useElapsed(startedAt: string | null): string {
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    if (startedAt === null) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [startedAt]);
  if (startedAt === null) return "0s";
  const ms = now - new Date(startedAt).getTime();
  if (Number.isNaN(ms) || ms < 0) return "0s";
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const remSec = seconds % 60;
  return `${mins}m ${remSec}s`;
}

/**
 * field-errors (2026-11-01, Mario's box 70 Q7) — the rows this form shows: the
 * schema's root fields (a variant's, for a one-of root). A refused field under
 * one of them is told by a readable form of its name ("Phone: use the
 * international format …"); the rows are labelled with the raw name, which the
 * line never repeats.
 */
function formRows(tree: FormTreeNode): FormRows {
  const roots = new Set<string>(
    tree.kind === "object"
      ? Object.keys(tree.properties)
      : tree.kind === "oneOf"
        ? tree.variants.flatMap((variant) => Object.keys(variant.node.properties))
        : [],
  );
  return (field) => (roots.has(field.split(/[.[]/u)[0] ?? "") ? "" : null);
}

interface ActionResultProps {
  readonly loading: boolean;
  readonly error: DispatchErrorState | null;
  /** Where the refused field may be named (`formRows`). */
  readonly rows: FormRows;
  readonly result: Record<string, unknown> | null;
  readonly replayed: boolean;
  readonly lastRequestId: string | null;
}

function ActionResult(props: ActionResultProps): JSX.Element | null {
  if (props.loading) {
    return <p className="action-result-loading">Awaiting response…</p>;
  }
  if (props.error !== null) {
    return (
      <div className="action-result action-result-error">
        <p>
          <strong>Error:</strong>{" "}
          <code className="tag tag-error">{props.error.code}</code>
        </p>
        <p>{failureText(props.error, props.rows)}</p>
        {props.lastRequestId !== null ? (
          <p className="action-request-id">
            request_id: <code>{props.lastRequestId}</code>
          </p>
        ) : null}
      </div>
    );
  }
  if (props.result !== null) {
    return (
      <div className="action-result action-result-ok">
        <p>
          <strong>OK</strong>
          {props.replayed ? (
            <span className="tag action-replay-badge">
              idempotency_replay_short_circuit
            </span>
          ) : null}
        </p>
        <details className="collapsible" open>
          <summary>Response payload</summary>
          <pre className="action-result-json">
            {JSON.stringify(props.result, null, 2)}
          </pre>
        </details>
        {props.lastRequestId !== null ? (
          <p className="action-request-id">
            request_id: <code>{props.lastRequestId}</code>
          </p>
        ) : null}
      </div>
    );
  }
  return null;
}
